import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Check, X, ChevronDown, ChevronUp, Globe, Package, Users, Shield, Key, UserX, UserCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Modal } from "../../components/shared/Modal";

const ALL_MODULES = [
  { id: "core", name: "Core", required: true },
  { id: "guest_portal", name: "Guest Portal" },
  { id: "memberships", name: "Memberships" },
  { id: "events", name: "Events" },
  { id: "event_commerce", name: "Event Commerce" },
  { id: "payments", name: "Payments" },
  { id: "analytics", name: "Analytics" },
  { id: "ticketing", name: "Event Ticketing" },
];

export default function ManageTenants() {
  const [tenants, setTenants] = useState([]);
  const [modules, setModules] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [form, setForm] = useState({ name: "", slug: "", domain: "", tagline: "", logo_url: "", modules: ["core"], stripe_key: "", email_sender: "", email_reply_to: "", email_resend_key: "" });
  const [pricingEdit, setPricingEdit] = useState(null);
  const [newPrice, setNewPrice] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const { authAxios } = useAuth();

  useEffect(() => { fetchAll(); }, []);
  const fetchAll = async () => {
    try {
      const [tRes, mRes] = await Promise.all([authAxios.get("/platform/tenants"), authAxios.get("/platform/modules")]);
      setTenants(tRes.data);
      setModules(mRes.data);
    } catch (err) { console.error("Request failed:", err?.message || err); }
  };

  const handleAdd = async () => {
    if (!form.name || !form.slug) return toast.error("Name and slug required");
    if (!form.domain) return toast.error("Domain is required");
    try {
      const res = await authAxios.post("/platform/tenants", {
        ...form,
        email_config: { resend_key: form.email_resend_key, sender_email: form.email_sender, reply_to: form.email_reply_to },
      });
      toast.success("Tenant created");
      setShowAdd(false);
      setForm({ name: "", slug: "", domain: "", tagline: "", logo_url: "", modules: ["core"], stripe_key: "", email_sender: "", email_reply_to: "", email_resend_key: "" });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleUpdate = async () => {
    try {
      await authAxios.put(`/platform/tenants/${editTenant.id}`, {
        ...form,
        email_config: { resend_key: form.email_resend_key, sender_email: form.email_sender, reply_to: form.email_reply_to },
      });
      toast.success("Tenant updated");
      setEditTenant(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleToggleActive = async (t) => {
    try {
      await authAxios.put(`/platform/tenants/${t.id}`, { is_active: !t.is_active });
      toast.success(t.is_active ? "Tenant deactivated" : "Tenant activated");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handlePriceUpdate = async (moduleId) => {
    try {
      await authAxios.put(`/platform/modules/${moduleId}/pricing`, { base_price: parseFloat(newPrice) });
      toast.success("Pricing updated");
      setPricingEdit(null); setNewPrice("");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File too large (max 2MB)");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files allowed");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setLogoUploading(true);
    try {
      const res = await authAxios.post("/platform/upload-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm(f => ({ ...f, logo_url: res.data.url }));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setLogoUploading(false);
    }
  };

  const MODULE_DEPS_MAP = {
    guest_portal: ["core"],
    memberships: ["core"],
    events: ["core"],
    event_commerce: ["core", "events"],
    payments: ["core"],
    analytics: ["core"],
    ticketing: ["core", "events"],
  };

  const toggleModule = (modId) => {
    if (modId === "core") return;
    setForm(f => {
      let newModules;
      if (f.modules.includes(modId)) {
        // Removing — also remove modules that depend on this one
        newModules = f.modules.filter(m => m !== modId);
        const toRemove = [];
        // event_commerce requires events + (guest_portal OR memberships)
        if (newModules.includes("event_commerce")) {
          if (modId === "events") toRemove.push("event_commerce");
          if ((modId === "guest_portal" || modId === "memberships") && !newModules.includes("guest_portal") && !newModules.includes("memberships")) toRemove.push("event_commerce");
        }
        // payments requires (memberships OR event_commerce)
        if (newModules.includes("payments")) {
          if ((modId === "memberships" || modId === "event_commerce") && !newModules.includes("memberships") && !newModules.includes("event_commerce")) toRemove.push("payments");
        }
        // ticketing requires events + (guest_portal or memberships)
        if (newModules.includes("ticketing")) {
          if (modId === "events") toRemove.push("ticketing");
          if ((modId === "guest_portal" || modId === "memberships") && !newModules.includes("guest_portal") && !newModules.includes("memberships")) toRemove.push("ticketing");
        }
        if (toRemove.length > 0) {
          newModules = newModules.filter(m => !toRemove.includes(m));
          toast.info(`Also removed: ${toRemove.join(", ")} (dependency)`);
        }
      } else {
        // Adding — also add required dependencies
        newModules = [...f.modules, modId];
        const toAdd = [];
        // event_commerce needs events + (guest_portal or memberships)
        if (modId === "event_commerce") {
          if (!newModules.includes("events")) toAdd.push("events");
          if (!newModules.includes("guest_portal") && !newModules.includes("memberships")) toAdd.push("guest_portal");
        }
        // payments needs memberships or event_commerce
        if (modId === "payments") {
          if (!newModules.includes("memberships") && !newModules.includes("event_commerce")) toAdd.push("memberships");
        }
        // ticketing needs events + (guest_portal or memberships)
        if (modId === "ticketing") {
          if (!newModules.includes("events")) toAdd.push("events");
          if (!newModules.includes("guest_portal") && !newModules.includes("memberships")) toAdd.push("guest_portal");
        }
        if (toAdd.length > 0) {
          newModules = [...new Set([...newModules, ...toAdd])];
          toast.info(`Also enabled: ${toAdd.join(", ")} (required)`);
        }
      }
      return { ...f, modules: newModules };
    });
  };

  const openEdit = (t) => {
    setForm({
      name: t.name, slug: t.slug, domain: t.domain || "", tagline: t.tagline || "",
      logo_url: t.logo_url || "", modules: t.modules || ["core"],
      stripe_key: t.stripe_key || "",
      email_sender: t.email_config?.sender_email || "",
      email_reply_to: t.email_config?.reply_to || "",
      email_resend_key: t.email_config?.resend_key || "",
    });
    setEditTenant(t);
  };

  const getModulePrice = (modId) => {
    const m = modules.find(x => x.module_id === modId);
    return m ? m.base_price : 0;
  };

  return (
    <div data-testid="manage-tenants">
      {/* Module Pricing */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-[#1e3a5f]">Module Pricing</h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
        {modules.map(m => (
          <div key={m.module_id} className="bg-white rounded-xl p-3 shadow-sm">
            <p className="text-xs font-bold text-[#1e3a5f]">{m.name}</p>
            {pricingEdit === m.module_id ? (
              <div className="flex gap-1 mt-1">
                <input type="number" value={newPrice} onChange={e => setNewPrice(e.target.value)} className="w-16 px-1 py-0.5 border rounded text-sm" />
                <button onClick={() => handlePriceUpdate(m.module_id)} className="text-green-500"><Check className="w-4 h-4" /></button>
                <button onClick={() => setPricingEdit(null)} className="text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <div className="flex items-center gap-1 mt-1">
                <p className="text-lg font-extrabold text-green-600">{"\u20ac"}{m.base_price}</p>
                <span className="text-[10px] text-slate-400">/mo</span>
                {!m.is_required && <button onClick={() => { setPricingEdit(m.module_id); setNewPrice(String(m.base_price)); }} className="ml-auto text-slate-400"><Edit2 className="w-3 h-3" /></button>}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Tenants */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-[#1e3a5f]">Tenants ({tenants.length})</h2>
        <button onClick={() => { setShowAdd(true); setForm({ name: "", slug: "", domain: "", tagline: "", logo_url: "", modules: ["core"], stripe_key: "", resend_key: "" }); }} className="w-10 h-10 rounded-full bg-[#8b5cf6] text-white flex items-center justify-center" data-testid="add-tenant-btn"><Plus className="w-5 h-5" /></button>
      </div>
      <div className="space-y-3">
        {tenants.map(t => (
          <div key={t.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${t.is_active ? "bg-green-100" : "bg-red-100"}`}>
                <Globe className={`w-5 h-5 ${t.is_active ? "text-green-600" : "text-red-500"}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#1e3a5f] truncate">{t.name}</p>
                <p className="text-xs text-slate-500 truncate">{t.slug} {t.domain ? `| ${t.domain}` : ""}</p>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                <span className="text-[10px] sm:text-xs font-medium text-slate-500 whitespace-nowrap hidden sm:inline">{t.modules?.length || 0} modules</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${t.is_active ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>{t.is_active ? "Active" : "Inactive"}</span>
                <button onClick={() => setExpanded(expanded === t.id ? null : t.id)}><ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded === t.id ? "rotate-180" : ""}`} /></button>
              </div>
            </div>
            {expanded === t.id && (
              <div className="px-4 pb-4 border-t bg-slate-50">
                <div className="flex flex-wrap gap-1 mt-3 mb-3">
                  {(t.modules || []).map(m => (
                    <span key={m} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#1e3a5f] text-white">{m}</span>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                  {t.tagline && <p className="col-span-2 text-slate-600 italic">{t.tagline}</p>}
                  <p><strong>Stripe:</strong> {t.stripe_key ? "Configured" : "Not set"}</p>
                  <p><strong>Resend:</strong> {t.email_config?.resend_key ? "Configured" : "Not set"}</p>
                  <p><strong>Sender:</strong> {t.email_config?.sender_email || "Not set"}</p>
                  <p><strong>Reply-to:</strong> {t.email_config?.reply_to || "Not set"}</p>
                  <p><strong>Monthly cost:</strong> {"\u20ac"}{(t.modules || []).reduce((sum, m) => sum + getModulePrice(m), 0)}/mo</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => openEdit(t)} className="flex items-center gap-1 text-blue-500 text-xs font-medium"><Edit2 className="w-3 h-3" /> Edit</button>
                  <button onClick={() => handleToggleActive(t)} className={`flex items-center gap-1 text-xs font-medium ${t.is_active ? "text-red-500" : "text-green-600"}`}>{t.is_active ? "Deactivate" : "Activate"}</button>
                </div>
                <TenantFAs tenantId={t.id} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {(showAdd || editTenant) && (
        <Modal title={editTenant ? "Edit Tenant" : "New Tenant"} onClose={() => { setShowAdd(false); setEditTenant(null); }}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Name *</label><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 rounded-xl border text-sm" /></div>
              <div><label className="text-xs text-slate-500">Slug *</label><input value={form.slug} onChange={e => setForm({...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")})} className="w-full px-3 py-2 rounded-xl border text-sm font-mono" disabled={!!editTenant} /></div>
            </div>
            <div><label className="text-xs text-slate-500">Domain <span className="text-red-500">*</span></label><input required value={form.domain} onChange={e => setForm({...form, domain: e.target.value.toLowerCase().trim()})} placeholder="e.g., my.example.org" className="w-full px-3 py-2 rounded-xl border text-sm" data-testid="tenant-domain-input" /></div>
            <div><label className="text-xs text-slate-500">Shop subtitle <span className="text-slate-400 font-normal">(shown under tenant name in the shop header — defaults to &quot;Event Shop&quot;)</span></label><input value={form.tagline} onChange={e => setForm({...form, tagline: e.target.value})} placeholder="Event Shop" className="w-full px-3 py-2 rounded-xl border text-sm" data-testid="tenant-subtitle-input" /></div>

            <div>
              <label className="text-xs text-slate-500">Logo</label>
              <div className="flex items-center gap-3 mt-1">
                {form.logo_url && (
                  <div className="p-2 bg-slate-50 rounded-lg shrink-0">
                    <img src={form.logo_url} alt="Preview" className="h-12 w-auto" onError={(e) => { e.target.style.display = "none"; }} data-testid="tenant-logo-preview" />
                  </div>
                )}
                <label className="flex-1 cursor-pointer">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={logoUploading}
                    data-testid="tenant-logo-upload-input"
                  />
                  <div className={`px-3 py-2 rounded-xl border border-dashed text-sm text-center ${logoUploading ? "bg-slate-100 text-slate-400" : "bg-white text-[#1e3a5f] hover:bg-slate-50"}`}>
                    {logoUploading ? "Uploading..." : (form.logo_url ? "Replace logo" : "Choose logo file")}
                  </div>
                </label>
                {form.logo_url && (
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, logo_url: "" }))}
                    className="text-xs text-red-500 underline shrink-0"
                    data-testid="tenant-logo-remove-btn"
                  >Remove</button>
                )}
              </div>
              <p className="text-[10px] text-slate-400 mt-1">PNG/JPG/SVG, recommended 400x400, max 2MB.</p>
            </div>

            <div>
              <label className="text-xs text-slate-500 mb-1 block">Modules</label>
              <div className="flex flex-wrap gap-1.5">
                {ALL_MODULES.map(m => (
                  <button key={m.id} onClick={() => toggleModule(m.id)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${form.modules.includes(m.id) ? "bg-[#1e3a5f] text-white" : "bg-slate-100 text-slate-500"} ${m.required ? "opacity-70 cursor-not-allowed" : ""}`}>
                    {m.name} {m.required ? "(required)" : `(\u20ac${getModulePrice(m.id)})`}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Stripe Key</label><input value={form.stripe_key} onChange={e => setForm({...form, stripe_key: e.target.value})} type="password" placeholder="sk_..." className="w-full px-3 py-2 rounded-xl border text-sm font-mono" /></div>
              <div><label className="text-xs text-slate-500">Resend API Key</label><input value={form.email_resend_key} onChange={e => setForm({...form, email_resend_key: e.target.value})} type="password" placeholder="re_..." className="w-full px-3 py-2 rounded-xl border text-sm font-mono" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Sender Email</label><input value={form.email_sender} onChange={e => setForm({...form, email_sender: e.target.value})} placeholder="notifications@domain.org" className="w-full px-3 py-2 rounded-xl border text-sm" /></div>
              <div><label className="text-xs text-slate-500">Reply-To Email</label><input value={form.email_reply_to} onChange={e => setForm({...form, email_reply_to: e.target.value})} placeholder="contact@domain.org" className="w-full px-3 py-2 rounded-xl border text-sm" /></div>
            </div>

            <button onClick={editTenant ? handleUpdate : handleAdd} className="w-full py-3 rounded-full bg-[#8b5cf6] text-white font-bold">
              {editTenant ? "Update Tenant" : "Create Tenant"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function TenantFAs({ tenantId }) {
  const { authAxios } = useAuth();
  const [fas, setFas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [resetInfo, setResetInfo] = useState(null); // { email, temp_password, email_sent }

  const load = async () => {
    setLoading(true);
    try { const r = await authAxios.get(`/platform/tenants/${tenantId}/finance-admins`); setFas(r.data || []); }
    catch (e) { setFas([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tenantId]);

  const create = async () => {
    if (!form.name || !form.email) return toast.error("Name and email required");
    try {
      const r = await authAxios.post(`/platform/tenants/${tenantId}/create-fa`, form);
      toast.success(`Created. Temp password: ${r.data.temp_password}`);
      setShowAdd(false); setForm({ name: "", email: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const toggleActive = async (fa) => {
    try { await authAxios.put(`/platform/tenants/${tenantId}/finance-admins/${fa.id}`, { is_active: !fa.is_active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const resetPw = async (fa) => {
    try {
      const r = await authAxios.post(`/platform/tenants/${tenantId}/finance-admins/${fa.id}/reset-password`);
      // Always show the temp password in-UI so the Sys Admin can share it even
      // if the email doesn't arrive (some tenants have no Resend key configured).
      setResetInfo({ email: fa.email, temp_password: r.data.temp_password, email_sent: r.data.email_sent });
      toast.success(r.data.message || "Password reset");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (fa) => {
    try { await authAxios.delete(`/platform/tenants/${tenantId}/finance-admins/${fa.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="mt-4 pt-3 border-t border-slate-200" data-testid={`tenant-fas-${tenantId}`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-bold text-[#1e3a5f]">Finance Admins ({fas.length})</h4>
        <button onClick={() => setShowAdd(true)} className="text-[10px] text-[#8b5cf6] font-medium flex items-center gap-1" data-testid={`add-fa-for-${tenantId}`}><Plus className="w-3 h-3" /> Add FA</button>
      </div>
      {loading && <p className="text-[10px] text-slate-400">Loading...</p>}
      {!loading && fas.length === 0 && <p className="text-[10px] text-slate-400 italic">No FAs assigned to this tenant.</p>}
      <div className="space-y-1.5">
        {fas.map(fa => (
          <div key={fa.id} className="bg-white rounded-lg p-2 flex items-center justify-between text-xs gap-2">
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#1e3a5f] truncate">{fa.name}</p>
              <p className="text-slate-400 text-[10px] truncate">{fa.email}</p>
            </div>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0 ${fa.is_active ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>{fa.is_active ? "Active" : "Inactive"}</span>
            <button onClick={() => resetPw(fa)} className="text-amber-500 p-1 shrink-0" title="Reset password"><Key className="w-3 h-3" /></button>
            <button onClick={() => toggleActive(fa)} className={`p-1 shrink-0 ${fa.is_active ? "text-red-400" : "text-green-500"}`} title={fa.is_active ? "Deactivate" : "Activate"}>
              {fa.is_active ? <UserX className="w-3 h-3" /> : <UserCheck className="w-3 h-3" />}
            </button>
            <button onClick={() => remove(fa)} className="text-red-400 p-1 shrink-0" title="Delete"><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
      {showAdd && (
        <Modal title="Add Finance Admin" onClose={() => setShowAdd(false)}>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">A welcome email with a temporary password will be sent.</p>
            <div><label className="text-xs text-slate-500">Name *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" data-testid="tenant-fa-name" /></div>
            <div><label className="text-xs text-slate-500">Email *</label><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" data-testid="tenant-fa-email" /></div>
            <button onClick={create} className="w-full py-3 rounded-full bg-[#8b5cf6] text-white font-bold" data-testid="save-tenant-fa">Create & Email Credentials</button>
          </div>
        </Modal>
      )}
      {resetInfo && (
        <Modal title="Temporary Password" onClose={() => setResetInfo(null)}>
          <div className="space-y-3" data-testid="fa-reset-password-dialog">
            <p className="text-xs text-slate-500">
              A password reset was requested for <strong>{resetInfo.email}</strong>.
              {resetInfo.email_sent
                ? " We've emailed the FA the new password."
                : " Email delivery FAILED — share this password with them directly."}
            </p>
            <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl p-4 text-center">
              <p className="text-[10px] text-slate-400 mb-1">Temporary password</p>
              <p className="text-2xl font-mono font-bold text-[#1e3a5f] tracking-widest select-all" data-testid="fa-temp-password">{resetInfo.temp_password}</p>
            </div>
            <p className="text-[10px] text-slate-400">
              The FA will be forced to set a new password on first login.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => { navigator.clipboard.writeText(resetInfo.temp_password); toast.success("Password copied"); }}
                className="flex-1 py-2 rounded-full bg-slate-200 text-slate-700 text-xs font-bold"
                data-testid="copy-fa-temp-password"
              >Copy</button>
              <button
                onClick={() => setResetInfo(null)}
                className="flex-1 py-2 rounded-full bg-[#1e3a5f] text-white text-xs font-bold"
                data-testid="close-fa-reset-dialog"
              >Done</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

